<template>
  <div class="fad-root">

    <!-- ══ Skeleton loader ══ -->
    <template v-if="loading && !attempt">
      <!-- Header skeleton -->
      <div class="fad-skeleton-header">
        <v-skeleton-loader type="heading" width="320" class="mb-1" />
        <v-skeleton-loader type="text"    width="200" />
      </div>
      <!-- Two-column detail panels -->
      <v-row dense class="mb-3">
        <v-col cols="12" md="6"><v-skeleton-loader type="card" height="260" /></v-col>
        <v-col cols="12" md="6">
          <v-skeleton-loader type="card" height="120" class="mb-3" />
          <v-skeleton-loader type="card" height="120" />
        </v-col>
      </v-row>
      <!-- Full-width linked cases panel -->
      <v-skeleton-loader type="card" height="140" />
    </template>

    <!-- ══ Loaded ══ -->
    <div v-else-if="attempt">

      <!-- ── Header ── -->
      <div class="fad-header">
        <button class="fad-back-btn" @click="$router.push({ name: 'flagged-attempts' })">
          <v-icon small>mdi-arrow-left</v-icon>
        </button>
        <div class="flex-grow-1 ml-3">
          <div class="fad-overline">Flagged Attempt</div>
          <div class="fad-title">
            {{ attempt.suspicionReason || 'Attempt detail' }}
          </div>
          <div class="fad-sub">
            Logged by {{ assigneeName(attempt.flaggedBy) }} on {{ fmtDate(attempt.dateCreated) }}
          </div>
        </div>
        <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
          <span class="fad-pill" :style="typePillStyle(attempt.passThroughType)">
            {{ formatType(attempt.passThroughType) }}
          </span>
          <span class="fad-pill" :style="attempt.caseLinks && attempt.caseLinks.length ? 'background:#EAF3DE;color:#3B6D11' : 'background:#F1EFE8;color:#5F5E5A'">
            <v-icon x-small style="margin-right:3px">{{ attempt.caseLinks && attempt.caseLinks.length ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
            {{ attempt.caseLinks && attempt.caseLinks.length ? 'Case linked' : 'Unlinked' }}
          </span>
          <div class="fad-header-divider" />
          <button class="fad-btn-primary" @click="editDialog = true">
            <v-icon x-small left>mdi-pencil-outline</v-icon>Edit details
          </button>
        </div>
      </div>

      <!-- ══ Row 1: Transaction details + Suspicion ══ -->
      <v-row dense class="mb-3">

        <!-- Transaction details -->
        <v-col cols="12" md="6">
          <div class="aml-panel aml-panel--full-height">
            <div class="aml-panel-head">
              <div class="aml-panel-icon" style="background:#E6F1FB">
                <v-icon small color="#185FA5">mdi-swap-horizontal</v-icon>
              </div>
              <span class="aml-panel-title">Transaction details</span>
            </div>
            <div class="pa-3">
              <div class="aml-grid-2 mb-2">
                <div class="aml-field"><div class="aml-label">Date</div><div class="aml-value">{{ attempt.transactionDate || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Amount</div><div class="aml-value">{{ attempt.currency }} ${{ attempt.amount ? Number(attempt.amount).toLocaleString() : '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Type</div><div class="aml-value">{{ attempt.transactionType || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Method</div><div class="aml-value">{{ attempt.transactionMethod || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Bank</div><div class="aml-value">{{ attempt.bank || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Cheque no.</div><div class="aml-value">{{ attempt.chequeNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Source of funds</div><div class="aml-value">{{ attempt.sourceOfFunds || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Report type</div><div class="aml-value">{{ attempt.reportType || '—' }}</div></div>
              </div>
              <div v-if="attempt.transactionDescription">
                <div class="aml-divider my-2" />
                <div class="aml-label">Description</div>
                <div class="aml-value mt-1">{{ attempt.transactionDescription }}</div>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Suspicion + Person -->
        <v-col cols="12" md="6">

          <!-- Suspicion -->
          <div class="aml-panel mb-3">
            <div class="aml-panel-head">
              <div class="aml-panel-icon" style="background:#FCEBEB">
                <v-icon small color="#A32D2D">mdi-alert-circle-outline</v-icon>
              </div>
              <span class="aml-panel-title">Suspicion</span>
            </div>
            <div class="pa-3">
              <div class="aml-field mb-2">
                <div class="aml-label">Reason</div>
                <div class="aml-value">{{ attempt.suspicionReason }}</div>
              </div>
              <div class="aml-field mb-2">
                <div class="aml-label">Category</div>
                <div class="aml-value">{{ attempt.suspicionCategory || '—' }}</div>
              </div>
              <div v-if="attempt.customerDescription" class="aml-field">
                <div class="aml-label">Customer description</div>
                <div class="aml-value">{{ attempt.customerDescription }}</div>
              </div>
            </div>
          </div>

          <!-- Person section -->
          <div v-if="showPersonSection" class="aml-panel">
            <div class="aml-panel-head">
              <div class="aml-panel-icon" style="background:#E1F5EE">
                <v-icon small color="#0F6E56">mdi-account-outline</v-icon>
              </div>
              <span class="aml-panel-title">{{ personSectionTitle }}</span>
            </div>
            <div class="pa-3">

              <!-- Account holder -->
              <div v-if="attempt.passThroughType === 'ACCOUNT_HOLDER'">
                <template v-if="accountPerson">
                  <div class="aml-grid-2">
                    <div class="aml-field"><div class="aml-label">Name</div><div class="aml-value">{{ accountPerson.firstName }} {{ accountPerson.lastName }}</div></div>
                    <div class="aml-field"><div class="aml-label">TRN</div><div class="aml-value">{{ accountPerson.trn || '—' }}</div></div>
                    <div v-if="accountPerson.dateOfBirth" class="aml-field"><div class="aml-label">Date of birth</div><div class="aml-value">{{ fmtDate(accountPerson.dateOfBirth) }}</div></div>
                    <div v-if="accountPerson.emailAddress" class="aml-field"><div class="aml-label">Email</div><div class="aml-value">{{ accountPerson.emailAddress }}</div></div>
                    <div class="aml-field" style="grid-column:span 2"><div class="aml-label">Account number</div><div class="aml-value">{{ attempt.account && attempt.account.mainAccountNumber }}</div></div>
                  </div>
                </template>
                <div v-else class="aml-label">Account holder details not available</div>
              </div>

              <!-- Non-account-holder -->
              <div v-if="attempt.passThroughType === 'NON_ACCOUNT_HOLDER'" class="aml-grid-2">
                <div class="aml-field"><div class="aml-label">Name</div><div class="aml-value">{{ attempt.firstName }} {{ attempt.lastName }}</div></div>
                <div class="aml-field"><div class="aml-label">TRN</div><div class="aml-value">{{ attempt.trn || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">ID</div><div class="aml-value">{{ attempt.idType }} {{ attempt.idNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Cell</div><div class="aml-value">{{ attempt.cellNumber || '—' }}</div></div>
                <div class="aml-field" style="grid-column:span 2"><div class="aml-label">Address</div><div class="aml-value">{{ [attempt.street, attempt.city, attempt.province, attempt.country].filter(Boolean).join(', ') || '—' }}</div></div>
              </div>

              <!-- Sender -->
              <div v-if="attempt.passThroughType === 'SENDER'" class="aml-grid-2">
                <div class="aml-field"><div class="aml-label">Name</div><div class="aml-value">{{ attempt.senderFirstName }} {{ attempt.senderLastName }}</div></div>
                <div class="aml-field"><div class="aml-label">TRN</div><div class="aml-value">{{ attempt.senderTRN || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">ID</div><div class="aml-value">{{ attempt.senderIdType }} {{ attempt.senderIdNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Account</div><div class="aml-value">{{ attempt.senderAccountNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Cell</div><div class="aml-value">{{ attempt.senderCellNumber || '—' }}</div></div>
                <div class="aml-field" style="grid-column:span 2"><div class="aml-label">Address</div><div class="aml-value">{{ [attempt.senderStreet, attempt.senderCity, attempt.senderProvince, attempt.senderCountry].filter(Boolean).join(', ') || '—' }}</div></div>
              </div>

              <!-- Receiver -->
              <div v-if="attempt.passThroughType === 'RECEIVER'" class="aml-grid-2">
                <div class="aml-field"><div class="aml-label">Name</div><div class="aml-value">{{ attempt.receiverFirstName }} {{ attempt.receiverLastName }}</div></div>
                <div class="aml-field"><div class="aml-label">TRN</div><div class="aml-value">{{ attempt.receiverTRN || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">ID</div><div class="aml-value">{{ attempt.receiverIdType }} {{ attempt.receiverIdNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Account</div><div class="aml-value">{{ attempt.receiverAccountNumber || '—' }}</div></div>
                <div class="aml-field"><div class="aml-label">Cell</div><div class="aml-value">{{ attempt.receiverCellNumber || '—' }}</div></div>
                <div class="aml-field" style="grid-column:span 2"><div class="aml-label">Address</div><div class="aml-value">{{ [attempt.receiverStreet, attempt.receiverCity, attempt.receiverProvince, attempt.receiverCountry].filter(Boolean).join(', ') || '—' }}</div></div>
              </div>

            </div>
          </div>

        </v-col>
      </v-row>

      <!-- ══ Row 2: Linked cases (full width) ══ -->
      <v-row dense class="mt-3">
        <v-col cols="12">
          <div class="aml-panel">
            <div class="aml-panel-head">
              <div class="aml-panel-icon" style="background:#EEEDFE">
                <v-icon small color="#534AB7">mdi-folder-outline</v-icon>
              </div>
              <span class="aml-panel-title">Linked cases</span>
              <span class="fad-count-pill ml-2">{{ attempt.caseLinks ? attempt.caseLinks.length : 0 }}</span>
              <v-spacer />
              <button class="fad-btn-primary" @click="openCasePickerDialog">
                <v-icon x-small left>mdi-link-plus</v-icon>Link case
              </button>
            </div>

            <div v-if="!attempt.caseLinks || !attempt.caseLinks.length" class="fad-empty-panel">
              Not linked to any case yet.
            </div>

            <div v-for="link in attempt.caseLinks" :key="link.id" class="fad-case-row">
              <div class="fad-case-icon">
                <v-icon small color="#534AB7">mdi-folder-outline</v-icon>
              </div>
              <div class="flex-grow-1" style="cursor:pointer" @click="goToCase(link.case && link.case.id)">
                <div class="aml-value" style="color:#534AB7;font-weight:600">{{ link.case && link.case.caseReference }}</div>
              </div>
              <span class="fad-pill mr-2" :style="caseStatusStyle(link.case && link.case.status)">
                {{ formatStatus(link.case && link.case.status) }}
              </span>
              <button class="fad-icon-btn" @click="confirmUnlink(link)" title="Unlink">
                <v-icon x-small color="#E24B4A">mdi-link-off</v-icon>
              </button>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Edit Details Dialog -->
    <v-dialog v-model="editDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="#854F0B">mdi-pencil-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">Edit details</span>
          </div>
          <v-btn icon @click="editDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="editForm">
            <v-autocomplete
              v-model="editForm.suspicionReason"
              :items="suspicionBehaviours"
              item-text="suspiciousBehaviour"
              item-value="suspiciousBehaviour"
              label="Suspicion reason"
              dense outlined class="mb-2"
              :loading="loadingBehaviours"
            />
            <v-select v-model="editForm.suspicionCategory" :items="suspicionCategories" label="Category" dense outlined clearable class="mb-2" />
            <v-text-field v-model="editForm.transactionDate" label="Transaction date" type="date" dense outlined class="mb-2" />
            <v-select v-model="editForm.currency" :items="currencies" label="Currency" dense outlined class="mb-2" />
            <v-text-field v-model="editForm.amount" label="Amount" type="number" dense outlined class="mb-2" />
            <v-text-field v-model="editForm.sourceOfFunds" label="Source of funds" dense outlined />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="submitEdit">
            <v-icon left small>mdi-content-save-outline</v-icon>Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Link Case Picker Dialog -->
    <v-dialog v-model="casePickerDialog" max-width="580" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-briefcase-plus</v-icon>Link to AML Case
          <v-spacer />
          <v-btn icon @click="casePickerDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <!-- Skeleton while loading available cases inside dialog -->
          <template v-if="loadingCases">
            <v-skeleton-loader type="list-item-two-line@3" class="mt-2" />
          </template>
          <template v-else>
            <div v-if="!availableCases.length" class="caption grey--text text-center py-4">No open cases found for this account.</div>
            <v-list v-else dense>
              <v-list-item
                v-for="c in availableCases"
                :key="c.id"
                :class="selectedCaseId === c.id ? 'primary lighten-5' : ''"
                style="cursor:pointer;border-radius:4px"
                class="mb-1"
                @click="selectedCaseId = c.id"
              >
                <v-list-item-action class="mr-2">
                  <v-icon :color="selectedCaseId === c.id ? 'primary' : 'grey'">{{ selectedCaseId === c.id ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-medium primary--text">{{ c.caseReference }}</v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ c.reportType }} &bull;
                    <v-chip x-small :color="statusColor(c.status)" dark class="ml-1">{{ formatStatus(c.status) }}</v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <span class="caption grey--text">{{ fmtDate(c.dateCreated) }}</span>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-text-field v-if="availableCases.length" v-model="caseLinkReason" label="Reason (optional)" dense outlined class="mt-3" />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="casePickerDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!selectedCaseId" :loading="linkingSaving" @click="submitLinkCase">Link</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unlink Confirmation -->
    <v-dialog v-model="unlinkDialog" max-width="420">
      <v-card>
        <v-card-title>Unlink from case?</v-card-title>
        <v-card-text>
          This removes the link between this flagged attempt and
          <strong>{{ selectedLink && selectedLink.case && selectedLink.case.caseReference }}</strong>.
          Neither record will be deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="unlinkDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="unlinking" @click="submitUnlink">Unlink</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import PageMixin    from '@/mixins/page.mixin';
import FormatMixin  from '@/mixins/format.mixin';
import AmlMixin     from '@/mixins/aml.mixin';
import { FlaggedAttemptService }      from '@/modules/amlpro/services/flagged-attempt.service';
import { AmlCaseService }             from '@/modules/amlpro/services/aml-case.service';
import { SuspiciousBehaviourService } from '@/modules/global-settings/aml/services/suspicious-behaviour.service';

const svc          = new FlaggedAttemptService();
const caseSvc      = new AmlCaseService();
const behaviourSvc = new SuspiciousBehaviourService();

export default {
  name: 'FlaggedAttemptDetail',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  props: { id: { type: String, default: null } },

  data() {
    return {
      loading: false, saving: false, loadingBehaviours: false,
      editDialog: false,
      attempt: null,
      suspicionBehaviours: [],

      editForm: { suspicionReason: '', suspicionCategory: '', transactionDate: '', currency: '', amount: '', sourceOfFunds: '' },

      casePickerDialog: false, loadingCases: false, availableCases: [],
      selectedCaseId: null, caseLinkReason: '', linkingSaving: false,

      unlinkDialog: false, unlinking: false, selectedLink: null,

      suspicionCategories: ['CASH','WIRE','STRUCTURING','IDENTITY','BEHAVIOUR','OTHER'],
      currencies:          ['JMD','USD','GBP','EUR','CAD','TTD','BBD'],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),
    attemptId() { return this.id || this.$route.params.id; },

    showPersonSection() { return !!this.attempt?.passThroughType; },

    personSectionTitle() {
      const m = { ACCOUNT_HOLDER: 'Account holder details', NON_ACCOUNT_HOLDER: 'Customer details', SENDER: 'Sender details', RECEIVER: 'Receiver details' };
      return m[this.attempt?.passThroughType] ?? 'Person details';
    },

    accountPerson() {
      const rows = this.attempt?.account?.person;
      if (!rows?.length) return null;
      const primary = rows.find(r => r.personClass === 'PRIMARY' || r.personClass === 'MEMBER');
      return (primary ?? rows[0])?.person ?? null;
    },
  },

  mounted() {
    this.loadAttempt();
    this.loadBehaviours();
  },

  methods: {
    typePillStyle(t) {
      const m = {
        ACCOUNT_HOLDER:     'background:#E6F1FB;color:#185FA5',
        NON_ACCOUNT_HOLDER: 'background:#E1F5EE;color:#0F6E56',
        SENDER:             'background:#FAEEDA;color:#854F0B',
        RECEIVER:           'background:#EEEDFE;color:#534AB7',
      };
      return m[t] || 'background:#F1EFE8;color:#5F5E5A';
    },
    caseStatusStyle(s) {
      const m = { OPEN: 'background:#E6F1FB;color:#185FA5', CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11', CLOSED_NO_ACTION: 'background:#F1EFE8;color:#5F5E5A', CLOSED_REPORT_FILED: 'background:#EEEDFE;color:#534AB7' };
      return m[s] || 'background:#F1EFE8;color:#5F5E5A';
    },

    async loadAttempt() {
      this.loading = true;
      try {
        const res    = await svc.fetchOne(this.attemptId);
        this.attempt = res.data ?? res;
        this.editForm = {
          suspicionReason:   this.attempt.suspicionReason,
          suspicionCategory: this.attempt.suspicionCategory ?? '',
          transactionDate:   this.attempt.transactionDate,
          currency:          this.attempt.currency,
          amount:            this.attempt.amount ?? '',
          sourceOfFunds:     this.attempt.sourceOfFunds ?? '',
        };
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load attempt' });
      } finally {
        this.loading = false;
      }
    },

    async loadBehaviours() {
      this.loadingBehaviours = true;
      try {
        const res = await behaviourSvc.fetchAll();
        this.suspicionBehaviours = (res.data ?? res).filter(b => b.isActive);
      } catch { /* silent */ } finally {
        this.loadingBehaviours = false;
      }
    },

    async submitEdit() {
      this.saving = true;
      try {
        const payload = { ...this.editForm };
        Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
        const res    = await svc.update(this.attemptId, payload);
        const result = res.data ?? res;
        Object.assign(this.attempt, result.updated ?? result);
        this.editDialog = false;
        this.showSnack({ color: 'success', title: 'Updated', message: 'Attempt updated' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Update failed' });
      } finally {
        this.saving = false;
      }
    },

    // Case picker
    async openCasePickerDialog() {
      this.casePickerDialog = true;
      this.selectedCaseId   = null;
      this.caseLinkReason   = '';
      this.loadingCases     = true;
      try {
        const accountId = this.attempt?.accountId;
        if (!accountId) { this.availableCases = []; return; }
        const res           = await caseSvc.fetchByAccount(accountId);
        const all           = res.data ?? res;
        const linkedCaseIds = new Set((this.attempt.caseLinks || []).map(l => l.caseId));
        this.availableCases = all.filter(c => !linkedCaseIds.has(c.id) && !this.isClosedStatus(c.status));
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load cases' });
      } finally {
        this.loadingCases = false;
      }
    },

    async submitLinkCase() {
      if (!this.selectedCaseId) return;
      this.linkingSaving = true;
      try {
        await caseSvc.addTransaction(this.selectedCaseId, { attemptId: this.attemptId, reason: this.caseLinkReason || undefined });
        await this.loadAttempt();
        this.casePickerDialog = false;
        this.selectedCaseId   = null;
        this.caseLinkReason   = '';
        this.showSnack({ color: 'success', title: 'Linked', message: 'Attempt linked to case' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Failed to link' });
      } finally {
        this.linkingSaving = false;
      }
    },

    // Unlink
    confirmUnlink(link) { this.selectedLink = link; this.unlinkDialog = true; },

    async submitUnlink() {
      if (!this.selectedLink) return;
      this.unlinking = true;
      try {
        await caseSvc.removeTransaction(this.selectedLink.caseId, this.selectedLink.id);
        this.attempt.caseLinks = this.attempt.caseLinks.filter(l => l.id !== this.selectedLink.id);
        this.unlinkDialog      = false;
        this.selectedLink      = null;
        this.showSnack({ color: 'success', title: 'Unlinked', message: 'Attempt unlinked from case' });
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to unlink' });
      } finally {
        this.unlinking = false;
      }
    },

    goToCase(id) { if (!id) return; this.$router.push({ name: 'aml-case-detail', params: { id } }); },
  },
};
</script>

<style scoped>
/* ── Root ── */
.fad-root { padding: 20px 24px 32px; }

/* ── Skeleton header placeholder ── */
.fad-skeleton-header { margin-bottom: 20px; }

/* ── Header ── */
.fad-header { display:flex; align-items:flex-start; gap:10px; margin-bottom:20px; flex-wrap:wrap; }
.fad-back-btn { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; margin-top:4px; }
.fad-back-btn:hover { background:#f5f5f5; }
.fad-overline { font-size:10px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#aaa; }
.fad-title { font-size:18px; font-weight:500; letter-spacing:-0.2px; margin:2px 0; max-width:420px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.fad-sub   { font-size:12px; color:#999; }

/* ── Buttons ── */
.fad-btn-primary { font-size:12px; padding:5px 12px; border-radius:8px; border:none; background:#185FA5; color:#fff; cursor:pointer; display:inline-flex; align-items:center; gap:4px; font-weight:500; font-family:inherit; transition:background 0.12s; }
.fad-btn-primary:hover:not(:disabled) { background:#0C447C; }
.fad-btn-primary:disabled { opacity:0.5; cursor:not-allowed; }

/* ── Cards ── */
.aml-panel { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-panel--full-height { height:100%; }
.aml-panel-head { display:flex; align-items:center; padding:12px 14px; border-bottom:0.5px solid #e0e0e0; gap:8px; }
.aml-panel-icon { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.aml-panel-title { font-size:13px; font-weight:500; }
.aml-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; margin-bottom:6px; }
.aml-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; }
.aml-field { }
.aml-label { font-size:11px; color:#aaa; margin-bottom:2px; }
.aml-value { font-size:12px; color:var(--color-text-primary, #1a1a1a); }
.aml-divider { height:0.5px; background:#e0e0e0; }

/* ── Cards ── */
.fad-card { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.fad-card-head { display:flex; align-items:center; padding:11px 14px; border-bottom:0.5px solid #e0e0e0; gap:8px; }
.fad-card-icon { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.fad-card-title { font-size:13px; font-weight:500; }

/* ── Field grid ── */
.fad-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; }
.fad-field  { }
.fad-label  { font-size:11px; color:#aaa; margin-bottom:2px; }
.fad-value  { font-size:12px; }

/* ── Pills ── */
.fad-pill { display:inline-flex; align-items:center; padding:3px 9px; border-radius:20px; font-size:11px; font-weight:500; white-space:nowrap; }
.fad-count-pill { font-size:10px; font-weight:700; padding:1px 7px; border-radius:20px; background:#EEEDFE; color:#534AB7; }

/* ── Linked cases rows ── */
.fad-case-row { display:flex; align-items:center; padding:10px 14px; border-bottom:0.5px solid #f0f0f0; gap:8px; }
.fad-case-row:last-child { border-bottom:none; }
.fad-case-row:hover { background:#f7f9fc; }
.fad-case-icon { width:28px; height:28px; border-radius:8px; background:#EEEDFE; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.fad-empty-panel { padding:20px; text-align:center; color:#aaa; font-size:12px; }

/* ── Misc ── */
.fad-header-divider { width:1px; height:24px; background:#e0e0e0; align-self:center; }
.fad-icon-btn { width:26px; height:26px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.fad-icon-btn:hover { background:#f0f0f0; }
</style>